import { Box, Flex, Heading, Tag, Icon, Text } from '@chakra-ui/react';
import { ViewIcon, SettingsIcon, CopyIcon, MinusIcon } from '@chakra-ui/icons';
import { GpuInfo } from '../../types/stats';
import { formatBytes } from '../../utils/stats';
import { FaPause, FaPlay, FaServer, FaDesktop } from "react-icons/fa";
import { FiCpu } from 'react-icons/fi';

interface GpuCardProps {
  data: GpuInfo;
  paused?: boolean;
}

export function GpuCard({ data, paused = false }: GpuCardProps) {
  const { controllers, displays } = data;

  return (
    <Box borderWidth="1px" borderRadius="2xl" p={4} position="relative" overflow="hidden">
      {paused && (
        <Tag borderRadius={"2xl"} position="absolute" top={2} right={2} colorScheme="yellow" display="flex" alignItems="center" gap={1}>
          <Icon as={MinusIcon} />
          Paused
        </Tag>
      )}
      
      <Box as="header" pb={3}>
        <Heading as="h3" size="md" display="flex" alignItems="center" gap={2}>
          {/* <Icon as={FaDesktop} color="blue.500" /> */}
          <FaDesktop/>
          GPU & Displays
        </Heading>
      </Box>
      
      <Box>
        {/* GPU Controllers */}
        {controllers.length > 0 && (
          <Box mb={6}>
            <Heading as="h4" size="sm" color="gray.500" display="flex" alignItems="center" gap={2} mb={3}>
              {/* <Icon as={SettingsIcon} /> */}
              <FiCpu/>
              Graphics Controllers
            </Heading>
            <Flex direction="column" gap={2}>
              {controllers.map((controller, index) => (
                <Box key={index} borderRadius="lg" p={3}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text fontWeight="medium">{controller.vendor}</Text>
                    <Tag borderRadius={"2xl"} variant="outline" fontSize="xs">
                      {controller.bus}
                    </Tag>
                  </Flex>
                  <Text fontSize="sm" color="gray.400">
                    {controller.model}
                  </Text>
                  {controller.vram > 0 && (
                    <Flex alignItems="center" gap={2} fontSize="sm" mt={2}>
                      <Icon as={FaServer} />
                      <Text color="gray.400">VRAM:</Text>
                      <Text fontFamily="mono">{formatBytes(controller.vram * 1024 * 1024)}</Text>
                    </Flex>
                  )}
                </Box>
              ))}
            </Flex>
          </Box>
        )}

        {/* Displays */}
        {displays.length > 0 && (
          <Box>
            <Heading as="h4" size="sm" color="gray.500" display="flex" alignItems="center" gap={2} mb={3}>
              <Icon as={ViewIcon} />
              Connected Displays
            </Heading>
            <Flex direction="column" gap={2}>
              {displays.map((display, index) => (
                <Box key={index} bg="gray.700" borderRadius="lg" p={3}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text fontWeight="medium">
                      {display.resolutionX} × {display.resolutionY}
                    </Text>
                    <Flex gap={1}>
                      {display.builtin && (
                        <Tag borderRadius={"2xl"} colorScheme="blue" fontSize="xs">
                          Built-in
                        </Tag>
                      )}
                      <Tag borderRadius={"2xl"} variant="outline" fontSize="xs">
                        {display.connection}
                      </Tag>
                    </Flex>
                  </Flex>
                  <Text fontSize="sm" color="gray.400">
                    {display.currentRefreshRate} Hz
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        )}

        {/* Empty State */}
        {controllers.length === 0 && displays.length === 0 && (
          <Box textAlign="center" py={4} color="gray.500">
            <Icon as={ViewIcon} boxSize={8} mx="auto" mb={2} opacity={0.5} />
            <Text fontSize="sm">No GPU information available</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
}
